import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Plug, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const TYPE_LABELS = { shopware6: "Shopware 6", magento2: "Magento 2", woocommerce: "WooCommerce" };
const TYPE_COLORS = { shopware6: "bg-blue-50 text-blue-700 border-blue-200", magento2: "bg-orange-50 text-orange-700 border-orange-200", woocommerce: "bg-purple-50 text-purple-700 border-purple-200" };

const EMPTY = { shop_id: "", type: "shopware6", base_url: "", label: "", enabled: true, client_id: "", client_secret: "", api_token: "", consumer_key: "", consumer_secret: "" };

export default function Integrations() {
    const { t } = useI18n();
    const [integrations, setIntegrations] = useState([]);
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [testing, setTesting] = useState(null);
    const [testResult, setTestResult] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: ints }, { data: sh }] = await Promise.all([
                api.get("/integrations"),
                api.get("/shops"),
            ]);
            setIntegrations(ints);
            setShops(sh);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const openCreate = () => { setEditing(null); setForm({ ...EMPTY, shop_id: shops[0]?.id || "" }); setOpen(true); };
    const openEdit = (i) => {
        setEditing(i);
        setForm({ ...EMPTY, ...i, client_secret: "", api_token: "", consumer_secret: "" });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (editing) {
                await api.put(`/integrations/${editing.id}`, payload);
            } else {
                await api.post("/integrations", payload);
            }
            toast.success(t("integration_saved"));
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("toast_save_failed"));
        }
    };

    const remove = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/integrations/${confirmDelete.id}`);
            toast.success(t("integration_deleted"));
            setConfirmDelete(null);
            load();
        } catch {
            toast.error(t("toast_delete_failed"));
        }
    };

    const testConnection = async (integration) => {
        setTesting(integration.id);
        setTestResult((r) => ({ ...r, [integration.id]: null }));
        try {
            await api.post(`/integrations/${integration.id}/test`);
            setTestResult((r) => ({ ...r, [integration.id]: true }));
            toast.success(t("integration_test_ok"));
        } catch (err) {
            setTestResult((r) => ({ ...r, [integration.id]: false }));
            toast.error(err?.response?.data?.detail || t("integration_test_failed"));
        } finally {
            setTesting(null);
        }
    };

    const shopName = (shop_id) => shops.find((s) => s.id === shop_id)?.name || shop_id;

    return (
        <div data-testid="integrations-page">
            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-[#00153f]">{t("integrations_title")}</h1>
                    <p className="text-gray-600 mt-1">{t("integrations_sub")}</p>
                </div>
                <Button onClick={openCreate} className="bg-[#0b905d] hover:bg-[#067a4d]" data-testid="btn-add-integration">
                    <Plus className="w-4 h-4 mr-2" /> {t("integration_add")}
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-gray-500">{t("loading")}</div>
            ) : integrations.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-md p-12 text-center bg-white" data-testid="integrations-empty">
                    <Plug className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <div className="font-display text-xl font-semibold text-[#00153f]">{t("integration_no_integrations")}</div>
                    <p className="text-gray-500 mt-2 max-w-sm mx-auto">{t("integration_no_integrations_sub")}</p>
                    <Button onClick={openCreate} className="mt-6 bg-[#0b905d] hover:bg-[#067a4d]">
                        <Plus className="w-4 h-4 mr-2" /> {t("integration_add")}
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {integrations.map((int) => (
                        <div key={int.id} data-testid={`integration-${int.id}`} className="border border-gray-200 rounded-md bg-white p-5 flex items-center gap-4 flex-wrap">
                            <div className={`text-xs font-mono font-semibold px-2 py-1 rounded border ${TYPE_COLORS[int.type] || "bg-gray-50 text-gray-700 border-gray-200"}`}>
                                {TYPE_LABELS[int.type] || int.type}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-[#00153f] text-sm">{int.label || shopName(int.shop_id)}</div>
                                <div className="text-xs text-gray-500 font-mono truncate">{int.base_url}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{t("integration_shop")}: {shopName(int.shop_id)}</div>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs">
                                {testResult[int.id] === true && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                                {testResult[int.id] === false && <XCircle className="w-4 h-4 text-red-500" />}
                                <span className={`px-2 py-0.5 rounded-full font-mono ${int.enabled ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                                    {int.enabled ? "aktiv" : "inaktiv"}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" disabled={testing === int.id} onClick={() => testConnection(int)} data-testid={`btn-test-${int.id}`}>
                                    {testing === int.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t("integration_test")}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(int)} data-testid={`btn-edit-int-${int.id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(int)} data-testid={`btn-delete-int-${int.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="integration-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? t("integration_edit_title") : t("integration_add")}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto flex-1 px-6 py-4">
                        <form id="integration-form" onSubmit={submit} className="space-y-4">
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_shop")}</Label>
                                <Select value={form.shop_id} onValueChange={(v) => setField("shop_id", v)}>
                                    <SelectTrigger className="mt-2" data-testid="int-shop"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_type")}</Label>
                                <Select value={form.type} onValueChange={(v) => setField("type", v)}>
                                    <SelectTrigger className="mt-2" data-testid="int-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="shopware6">Shopware 6</SelectItem>
                                        <SelectItem value="magento2">Magento 2</SelectItem>
                                        <SelectItem value="woocommerce">WooCommerce</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_base_url")} *</Label>
                                <Input data-testid="int-url" required placeholder="https://mein-shop.de" value={form.base_url} onChange={(e) => setField("base_url", e.target.value)} className="mt-2" />
                            </div>
                            <div>
                                <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_label")}</Label>
                                <Input data-testid="int-label" value={form.label} onChange={(e) => setField("label", e.target.value)} className="mt-2" />
                            </div>

                            {form.type === "shopware6" && (
                                <>
                                    <div>
                                        <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_client_id")}</Label>
                                        <Input data-testid="int-client-id" value={form.client_id} onChange={(e) => setField("client_id", e.target.value)} className="mt-2" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_client_secret")}</Label>
                                        {editing?.has_secret && <p className="text-xs text-gray-500 mt-1">{t("integration_secret_set")}</p>}
                                        <Input data-testid="int-client-secret" type="password" placeholder={editing?.has_secret ? t("integration_secret_placeholder") : ""} value={form.client_secret} onChange={(e) => setField("client_secret", e.target.value)} className="mt-2" />
                                    </div>
                                </>
                            )}

                            {form.type === "magento2" && (
                                <div>
                                    <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_api_token")}</Label>
                                    {editing?.has_secret && <p className="text-xs text-gray-500 mt-1">{t("integration_secret_set")}</p>}
                                    <Input data-testid="int-api-token" type="password" placeholder={editing?.has_secret ? t("integration_secret_placeholder") : ""} value={form.api_token} onChange={(e) => setField("api_token", e.target.value)} className="mt-2" />
                                </div>
                            )}

                            {form.type === "woocommerce" && (
                                <>
                                    <div>
                                        <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_consumer_key")}</Label>
                                        <Input data-testid="int-consumer-key" value={form.consumer_key} onChange={(e) => setField("consumer_key", e.target.value)} className="mt-2" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">{t("integration_consumer_secret")}</Label>
                                        {editing?.has_secret && <p className="text-xs text-gray-500 mt-1">{t("integration_secret_set")}</p>}
                                        <Input data-testid="int-consumer-secret" type="password" placeholder={editing?.has_secret ? t("integration_secret_placeholder") : ""} value={form.consumer_secret} onChange={(e) => setField("consumer_secret", e.target.value)} className="mt-2" />
                                    </div>
                                </>
                            )}

                            <div className="flex items-center gap-3 pt-1">
                                <Switch checked={form.enabled} onCheckedChange={(v) => setField("enabled", v)} data-testid="int-enabled" />
                                <Label>{t("integration_enabled")}</Label>
                            </div>
                        </form>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
                        <Button type="submit" form="integration-form" data-testid="int-submit" className="bg-[#0b905d] hover:bg-[#067a4d]">{t("save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("integration_delete_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("integration_delete_desc")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-integration">{t("delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
